import React, { useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { Content, DisplayConfiguration, displayConfiguration } from './Content';
import { ToolTipWrapper } from './Tooltip.styles';
import { Direction } from '../types';
import { calculatePosition } from '../utils';

export type Props = React.PropsWithChildren<{
  /**
   * Title content rendered in tooltip.
   */
  title: ReactNode;
  /** Optional content component */
  content?: ReactNode;
  /** Set tooltip position relative to element */
  direction?: Direction;
  /** Include tooltip arrow */
  showArrow?: boolean;
  /** Include icon preceding title */
  showIcon?: boolean;
  /**
   * Require user click event to activate tooltip; disables default hover behavior.
   */
  persist?: boolean;
  /**
   * Tooltip display configuration options.
   *
   * Unassigned properties will use default values.
   */
  display?: Partial<DisplayConfiguration>;
  /** Class names for elements */
  classNames?: {
    tooltip?: string;
  };
}>;

const Tooltip = ({
  classNames,
  showArrow = true,
  showIcon = false,
  direction = 'bottom',
  persist = false,
  display,
  title,
  content,
  children
}: Props) => {
  const tooltipRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLElement>(null);
  const currentContentRef = contentRef.current;
  const leaveTimeoutRef = useRef(0);
  const [displayTooltip, setDisplayTooltip] = useState(false);
  const [persistOnClick, setPersistOnClick] = useState(false);
  const [position, setPosition] = useState({});
  const tooltipSettings = useMemo(
    () => ({ ...displayConfiguration, ...display }),
    [display]
  );

  const showTooltip = e => {
    e.stopPropagation();
    clearTimeout(leaveTimeoutRef.current);
    if (persistOnClick) return;
    setDisplayTooltip(true);
  };
  const hoverOffTooltip = e => {
    e.stopPropagation();
    leaveTimeoutRef.current = window.setTimeout(
      () => setDisplayTooltip(false),
      250
    );
  };

  const closeToolTip = (e: MouseEvent | TouchEvent) => {
    e.stopPropagation();
    setDisplayTooltip(false);
    setPersistOnClick(false);
  };

  const setPersistTooltip = e => {
    e.stopPropagation();
    showTooltip(e);
    setPersistOnClick(v => !v);
  };

  const clearHideTooltip = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearTimeout(leaveTimeoutRef.current);
  };

  useEffect(() => {
    function handleClickElsewhere(e) {
      if (contentRef.current?.contains(e.target)) return;

      setDisplayTooltip(false);

      if (!tooltipRef.current?.contains(e.target)) {
        setPersistOnClick(false);
      }
    }

    if (displayTooltip) {
      document.addEventListener('click', handleClickElsewhere, true);

      setPosition(
        calculatePosition(
          direction,
          (tooltipRef.current as any).getBoundingClientRect(),
          (contentRef.current as any).getBoundingClientRect(),
          tooltipSettings
        )
      );
    }

    return () => {
      document.removeEventListener('click', handleClickElsewhere);
      clearTimeout(leaveTimeoutRef.current);
    };
  }, [direction, displayTooltip, tooltipSettings]);

  useEffect(() => {
    if (!currentContentRef) return;

    // Set css Property for tailwind to pick up on in Content.styles.ts
    currentContentRef.style.setProperty(
      '--arrow-offset-x',
      `${tooltipSettings.arrowOffsetX}px`
    );
    currentContentRef.style.setProperty(
      '--arrow-offset-y',
      `${tooltipSettings.arrowOffsetY}px`
    );
  }, [
    tooltipSettings.arrowOffsetY,
    tooltipSettings.arrowOffsetX,
    currentContentRef
  ]);

  return (
    <ToolTipWrapper
      data-analytics-id={null}
      onMouseEnter={e => !(persist || persistOnClick) && showTooltip(e)}
      onMouseLeave={e => !(persist || persistOnClick) && hoverOffTooltip(e)}
      onClick={setPersistTooltip}
      ref={tooltipRef}
    >
      {displayTooltip && (
        <Content
          {...tooltipSettings}
          {...position}
          classNames={classNames}
          hoverCallback={clearHideTooltip}
          persist={persist}
          showArrow={showArrow}
          showIcon={showIcon}
          direction={direction}
          closeCallback={closeToolTip}
          ref={contentRef}
          title={title}
          content={content}
        />
      )}
      {children}
    </ToolTipWrapper>
  );
};

export default Tooltip;
